import { WIDOW_SIZE } from "@bing/engine-shelem";
import type { CSSProperties, ReactNode } from "react";
import { Card } from "../components/Card";
import { collectSweep } from "../game/card-motion";
import { Departing } from "../game/departing";
import { SEAT_AVATAR } from "../game/felt-geometry";
import { KAF_DISCARD_SWEEP_MS, KAF_FLIGHT_MS, KAF_STAGGER_MS } from "../game/shelem-beats";
import { fa } from "../i18n/fa";
import {
  BADGE_GAP,
  badgeHeight,
  CENTRE_AIR_PX,
  NAME_LINE_PX,
  seatRootHeight,
} from "./shelem-auction";
import {
  FELT_INSET,
  PARTNER_TOP_IN_FELT,
  shelemSideNameMaxPx,
  WIDOW_SCALE,
  WIDOW_STEP,
} from "./shelem-felt";

/* ------------------------------------------------------------------ *
 * شلم's کف ON THE MOVE (P1 Task 5) — the four flying from the felt to the حاکم, and the four laid
 * down going to its pile — drawn by `ShelemTable` on the live frames `useKafMoments` names. Moved
 * here unchanged (P1 Task 6, a pure move: the table had grown back to ~1450 lines).
 * ------------------------------------------------------------------ */

/** Where the felt's کف sat until card-67 (and still sits where the corner cannot hold it — the
 *  `partner` form): its four cards' box, fanned, beside your partner's avatar. */
const KAF_SPOT_W = 36 * WIDOW_SCALE + (WIDOW_SIZE - 1) * WIDOW_STEP;
const KAF_SPOT: CSSProperties = {
  top: PARTNER_TOP_IN_FELT,
  right: `calc(50% + ${SEAT_AVATAR.regular / 2 + 6}px)`,
  height: 50 * WIDOW_SCALE,
  width: KAF_SPOT_W,
};

/** The کف's four, fanned as the partner form fans them — face-down, or (yours) face-up. */
function kafFan(i: number, face: string | null): ReactNode {
  return (
    <span
      className="block"
      style={{
        transformOrigin: "bottom left",
        transform: `scale(${WIDOW_SCALE}) rotate(${(i - (WIDOW_SIZE - 1) / 2) * 3}deg)`,
      }}
    >
      {face ? <Card id={face} small /> : <Card id="" faceDown small />}
    </span>
  );
}

/*
 * THE کف IN THE FELT'S TOP-RIGHT CORNER (card-67 C4 — the operator: «The 4 cards for the حاکم are
 * small and sit next to the partner's avatar. Show them bigger, side by side, in the empty top-right
 * corner of the table, and move them to whoever becomes حاکم with a good motion.»). While the auction
 * runs the four lie face-down SIDE BY SIDE (`KAF_CORNER_GAP` apart, upright) in the felt's PHYSICAL
 * top-right corner — `KAF_CORNER_TOP` down and `KAF_CORNER_RIGHT` in, so the last card's corner keeps
 * 4 px of air inside the felt's 40 px rounded corner (26 / 24 px from the arc's centre: 35.4 of 36) —
 * as wide as the corner holds (`kafCornerPlacement`): from 16 px past the partner's ring (and the
 * dealer's marker on its upper-right) to the corner, and down to the partner's name box or the RIGHT
 * seat's bid badge, whichever is higher, 4 px clear. Sized for the felt as it is on YOUR bid turn — the auction panel up
 * (`auctionPanelHeight`), the felt's shortest in the auction — so the four keep ONE size through the
 * whole auction (the panel opening on your turn moves nothing in the corner). A `small` card (36×50)
 * at most; where the corner cannot hold even the cards the felt drew before (20.16 px wide — 320×568
 * under the bidding row; Telegram Desktop's best-effort sizes), they stay where they were, fanned
 * beside the partner's avatar (`partner`): the one band every size keeps free.
 *
 * card-98 item 9 (the operator, 2026-10-27: «The حاکم's 4 cards in the table corner: fan them in a
 * curve, corners overlapping, the arc toward the table's centre, so they feel like real cards.» — and on
 * the prototype, 10-28: «not a real fan — just the 4 cards overlapping, with a slight curve toward the
 * table's centre. They're only for show, so where space is tight, 307×472 included, they can overlap
 * more and stay in the corner instead of going back beside the partner.»): the four no longer lie side
 * by side — each lies `step` px after the one before, OVER it (`KAF_STEP_MAX` of a card's width at
 * most: always overlapping), on a SLIGHT curve: `KAF_CURVE_DEG` more turn a card, their centres on the
 * arc that turn draws, so the curve of their feet bows DOWN, toward the felt's middle
 * (`kafCornerLayout`). The size is the row's (one size through the auction, as before) wherever it fits
 * the corner's height; where the width is short they overlap MORE (down to `KAF_STEP_MIN`), never
 * smaller. Where the corner above the right seat has no height at all (Telegram Desktop 307×472 and the
 * like, the felt under 60 px on your bid turn) they lie in the corner BESIDE the right seat — between
 * it and the partner, `KAF_TOP_MIN` from the felt's top — at the partner form's size. Only where even
 * that cannot hold them (a felt with no height left) do they go back beside the partner.
 */
export const KAF_CORNER_TOP = 16;
export const KAF_CORNER_RIGHT = 14;
/** The side-by-side row's gap (card-67 C4) — still what the row's size is computed with (see above). */
export const KAF_CORNER_GAP = 3;
/** The slight curve: a card turns this much more than the one before it (deg). */
export const KAF_CURVE_DEG = 4;
/** A card's step after the one before, as a share of a card's width: at most (roomy) — always under
 *  the next — and at least (tight: the operator's «they can overlap more»). */
export const KAF_STEP_MAX = 0.6;
export const KAF_STEP_MIN = 0.28;
/** The highest the corner's box may rise (felt px) where `KAF_CORNER_TOP` leaves it too little height. */
export const KAF_TOP_MIN = 8;
/** A `small` card at its own size: the largest the corner draws. */
const KAF_CARD_MAX_W = 36;
/** The cards the felt drew before card-67 (a `small` card at `WIDOW_SCALE`): never smaller in the corner. */
export const KAF_CARD_MIN_W = 36 * WIDOW_SCALE;
/** The air the corner keeps from a seat part below it (the partner's name box, the right seat's badge). */
const KAF_AIR = 4;
/** …and from the partner's ring and marker beside it: the four lie IN THE CORNER, not at the
 *  partner's side (the operator: «they sit next to the partner's avatar»). */
const KAF_PARTNER_AIR = 16;
/** The dealer's marker hangs 4 px past its ring's side (`AVATAR_CORNER`'s `-right-1`). */
const MARKER_OVERHANG = 4;
const CARD_ASPECT = 50 / 36;

/** One card of the corner: its centre in the corner's box (px) and its turn (deg, clockwise). */
export interface KafFanCard {
  readonly x: number;
  readonly y: number;
  readonly deg: number;
}

/**
 * The corner's `count` cards, `cardW` wide, each `step` px after the one before (left to right, the
 * later one over it), on the slight curve: card i turns `(i − mid) × KAF_CURVE_DEG` the hanging way —
 * an outer card's foot swings out — and its centre lies on the arc that turn draws round a point
 * above them (a radius of `step` per turn), so the outer ones ride a little higher and the curve of
 * the feet bows down, toward the felt's middle. Returns the box (the turned cards' bounds) and each
 * card in it.
 */
export function kafCornerLayout(
  cardW: number,
  step: number,
  count: number,
): { readonly w: number; readonly h: number; readonly cards: readonly KafFanCard[] } {
  const h = cardW * CARD_ASPECT;
  const turn = (KAF_CURVE_DEG * Math.PI) / 180;
  const r = step / turn;
  const raw = Array.from({ length: count }, (_u, i) => {
    const phi = (i - (count - 1) / 2) * turn;
    return { x: r * Math.sin(phi), y: r * Math.cos(phi), phi };
  });
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const c of raw) {
    // the card turned by −phi about its centre
    const cos = Math.cos(-c.phi);
    const sin = Math.sin(-c.phi);
    for (const [a, b] of [
      [-cardW / 2, -h / 2],
      [cardW / 2, -h / 2],
      [cardW / 2, h / 2],
      [-cardW / 2, h / 2],
    ] as const) {
      const px = c.x + a * cos - b * sin;
      const py = c.y + a * sin + b * cos;
      minX = Math.min(minX, px);
      minY = Math.min(minY, py);
      maxX = Math.max(maxX, px);
      maxY = Math.max(maxY, py);
    }
  }
  return {
    w: maxX - minX,
    h: maxY - minY,
    cards: raw.map((c) => ({ x: c.x - minX, y: c.y - minY, deg: (-c.phi * 180) / Math.PI })),
  };
}

/** Where the felt's کف lies while the auction runs: in the corner at a card width and step, or —
 *  where not even the corner beside the right seat can hold it — beside the partner, as before. */
export interface KafCorner {
  readonly form: "corner" | "partner";
  /** A card's width (px); its height is 50 / 36 of it. */
  readonly cardW: number;
  /** A card's step after the one before (px) — less than `cardW`: they overlap. */
  readonly step: number;
  /** The corner box's top and its right inset (felt px). */
  readonly top: number;
  readonly right: number;
}

/** A side seat's root width (`ShelemSideSeat`) on a felt `feltW` wide: `TableSeat`'s 52 px at least,
 *  else its name box's cap (`shelemSideNameMaxPx`, SIDE_NAME_MAX's one px form). */
const sideSeatW = (feltW: number): number => Math.max(52, shelemSideNameMaxPx(feltW));
/** The side seat's root sits 16 px in from the table's edge — 4 px inside the felt's. */
const SIDE_SEAT_IN_FELT = 16 - FELT_INSET;
/** The corner box beside the right seat keeps this off the felt's foot. */
const KAF_FOOT_AIR = 6;

/** The cards in one corner region (felt px), `wMax` wide wherever the box fits under the corner's
 *  top — risen as far as `KAF_TOP_MIN` to keep that size — else as wide as it fits there, never under
 *  the partner form's size; at the widest step the width holds (never past `KAF_STEP_MAX`, never
 *  under `KAF_STEP_MIN`). Null where it cannot hold them. */
function placeIn(
  region: { left: number; right: number; bottom: number },
  wMax: number,
  inset: number,
): KafCorner | null {
  // the box's height per card width, at the widest step (the tallest: the curve's radius grows with it)
  const unitH = kafCornerLayout(1, KAF_STEP_MAX, WIDOW_SIZE).h;
  const raw = Math.min(wMax, (region.bottom - KAF_TOP_MIN) / unitH);
  if (!(raw >= KAF_CARD_MIN_W - 1e-9)) return null;
  const w = Math.max(KAF_CARD_MIN_W, Math.floor(raw * 100) / 100);
  for (let k = KAF_STEP_MAX; k >= KAF_STEP_MIN - 1e-9; k -= 0.02) {
    const step = Math.floor(k * w * 100) / 100;
    const box = kafCornerLayout(w, step, WIDOW_SIZE);
    if (box.w > region.right - region.left) continue;
    const top = Math.max(KAF_TOP_MIN, Math.min(KAF_CORNER_TOP, region.bottom - box.h));
    return { form: "corner", cardW: w, step, top: Math.floor(top * 100) / 100, right: inset };
  }
  return null;
}

/** The corner's cards on a felt `feltW` wide and `feltHMin` tall on your bid turn (see above). */
export function kafCornerPlacement(
  feltW: number | null,
  feltHMin: number | null,
  compact: boolean,
  zoom: number,
  /** The right seat carries a notice: it wraps a line taller, its badge half a line higher. */
  rightNotice = false,
): KafCorner {
  const partner: KafCorner = {
    form: "partner",
    cardW: KAF_CARD_MIN_W,
    step: WIDOW_STEP,
    top: KAF_CORNER_TOP,
    right: KAF_CORNER_RIGHT,
  };
  if (feltW === null || feltHMin === null || !(feltW > 0) || !(feltHMin > 0)) return partner;
  const ring = (compact ? SEAT_AVATAR.compact : SEAT_AVATAR.regular) + 4;
  const left = feltW / 2 + ring / 2 + MARKER_OVERHANG + KAF_PARTNER_AIR;
  const partnerNameTop = PARTNER_TOP_IN_FELT + ring + (compact ? 4 : 6);
  const rootH = seatRootHeight(compact, zoom) + (rightNotice ? NAME_LINE_PX * zoom : 0);
  const rightBadgeTop = feltHMin / 2 - rootH / 2 - BADGE_GAP - badgeHeight(zoom);
  // ① the corner proper, ABOVE the right seat (card-67 C4's): at the side-by-side row's size where
  //    its height holds it — one size through the auction, as before
  const right = feltW - KAF_CORNER_RIGHT;
  const bottom = Math.min(partnerNameTop, rightBadgeTop) - KAF_AIR;
  const rowW = Math.min(
    KAF_CARD_MAX_W,
    (right - left - (WIDOW_SIZE - 1) * KAF_CORNER_GAP) / WIDOW_SIZE,
    (bottom - KAF_CORNER_TOP) / CARD_ASPECT,
  );
  const above = placeIn({ left, right, bottom }, Math.max(rowW, KAF_CARD_MIN_W), KAF_CORNER_RIGHT);
  if (above !== null) return above;
  // ② …else BESIDE the right seat — between its column and the partner, the felt's foot (and the
  //    partner's name box, the auction's statement under it) below — at the partner form's size
  const inset = SIDE_SEAT_IN_FELT + sideSeatW(feltW) + KAF_AIR;
  const statementTop = PARTNER_TOP_IN_FELT + seatRootHeight(compact, zoom) + CENTRE_AIR_PX;
  const beside = placeIn(
    {
      left,
      right: feltW - inset,
      bottom: Math.min(partnerNameTop - KAF_AIR, statementTop - KAF_AIR, feltHMin - KAF_FOOT_AIR),
    },
    KAF_CARD_MIN_W,
    inset,
  );
  return beside ?? partner;
}

/** The corner's box: the four, right-anchored in the felt's top-right corner. */
function cornerBox(corner: KafCorner, count: number): CSSProperties {
  const box = kafCornerLayout(corner.cardW, corner.step, count);
  return { top: corner.top, right: corner.right, width: box.w, height: box.h };
}

/** A fanned card's own box in the corner's (upright, centred where the card lies — so a flight that
 *  starts from it aims from the card's real centre), and its turn. */
function fanCardBox(card: KafFanCard, cardW: number): CSSProperties {
  const h = cardW * CARD_ASPECT;
  return { left: card.x - cardW / 2, top: card.y - h / 2, width: cardW, height: h };
}

/** A card of the fan: the corner's card turned by its step about its own centre (the box is sized
 *  as the card is DRAWN — the scaled card inside lays out at 36×50 — so «centre» is the card's). */
function fanned(card: KafFanCard, cardW: number, face: string | null): ReactNode {
  return (
    <span
      className="block"
      style={{
        width: cardW,
        height: cardW * CARD_ASPECT,
        transformOrigin: "center",
        transform: `rotate(${card.deg.toFixed(2)}deg)`,
      }}
    >
      {cornerCard(cardW, face)}
    </span>
  );
}

/** One of the corner's cards: a `small` card scaled to `cardW` from its top-left, in a box that size. */
function cornerCard(cardW: number, face: string | null): ReactNode {
  return (
    <span
      className="block"
      style={{ transformOrigin: "top left", transform: `scale(${cardW / 36})` }}
    >
      {face ? <Card id={face} small /> : <Card id="" faceDown small />}
    </span>
  );
}

/**
 * THE FACE-DOWN کف while the auction runs (P1 Task 4; card-67 C4): the four in the felt's top-right
 * corner (`kafCornerPlacement`), or fanned beside the partner's avatar where the corner cannot hold
 * them (`partner`: its left, the end side under rtl, at the ring's height — 26 px left of the felt's
 * middle). Drawn off the view alone (`widowCount`), so a reconnect or a late frame lands in place.
 */
export function WidowCorner({ count, corner }: { count: number; corner: KafCorner }): ReactNode {
  if (corner.form === "corner") {
    const fan = kafCornerLayout(corner.cardW, corner.step, count);
    return (
      <div
        role="img"
        aria-label={fa.shelem.widow}
        data-kaf-form="corner"
        data-kaf-fan=""
        className="pointer-events-none absolute z-10"
        style={cornerBox(corner, count)}
      >
        {fan.cards.map((card, i) => (
          <span key={i} className="absolute" style={fanCardBox(card, corner.cardW)}>
            {fanned(card, corner.cardW, null)}
          </span>
        ))}
      </div>
    );
  }
  return (
    <div
      role="img"
      aria-label={fa.shelem.widow}
      data-kaf-form="partner"
      className="pointer-events-none absolute z-10"
      style={{
        top: PARTNER_TOP_IN_FELT,
        right: `calc(50% + ${SEAT_AVATAR.regular / 2 + 6}px)`,
        height: 50 * WIDOW_SCALE,
        width: 36 * WIDOW_SCALE + (count - 1) * WIDOW_STEP,
      }}
    >
      {Array.from({ length: count }).map((_u, i) => (
        <span
          key={i}
          className="absolute bottom-0"
          style={{
            left: i * WIDOW_STEP,
            transformOrigin: "bottom left",
            transform: `scale(${WIDOW_SCALE}) rotate(${(i - (count - 1) / 2) * 3}deg)`,
          }}
        >
          <Card id="" faceDown small />
        </span>
      ))}
    </div>
  );
}

/**
 * Where a card lands at the حاکم (felt coordinates, a felt `w` × `h` px): the partner's ring under
 * `PARTNER_TOP_IN_FELT`, a side seat's ring ~30 px in from its edge and ~15 px above the felt's
 * middle, your hand below the felt.
 */
function kafLanding(rel: number, w: number, h: number): [number, number] {
  return rel === 2
    ? [w / 2, PARTNER_TOP_IN_FELT + 2 + SEAT_AVATAR.regular / 2]
    : rel === 1
      ? [w - 30, h / 2 - 15]
      : rel === 3
        ? [30, h / 2 - 15]
        : [w / 2, h + 40]; // you: into your hand, below the felt
}

/** The pickup's vector from the old spot beside the partner (the `partner` form): from the کف's centre
 *  (45.08 px left of the felt's middle, 14 px below the partner's ring top) to the حاکم's avatar. */
function kafPickupVector(rel: number, w: number, h: number): { x: number; y: number } {
  const fromX = w / 2 - SEAT_AVATAR.regular / 2 - 6 - KAF_SPOT_W / 2;
  const fromY = PARTNER_TOP_IN_FELT + 14;
  const [toX, toY] = kafLanding(rel, w, h);
  return { x: Math.round(toX - fromX), y: Math.round(toY - fromY) };
}

/**
 * The felt's size NOW — read off `main` (the felt sits `FELT_INSET` inside it) the moment the flight
 * starts, the frame it flies in already laid out: the declaring frame swaps the panel (the bidding
 * row goes, the HUD and the confirm come), so a felt measured before it is the auction's and aimed a
 * side seat's cards 20–40 px off its avatar (P1 Task 6, the Task 5 review's M4). Null when nothing is
 * laid out (happy-dom, a hidden page): the cards then stay put.
 */
function feltNow(el: HTMLElement): { w: number; h: number } | null {
  const main = el.closest("main");
  if (!main || typeof window.getComputedStyle !== "function") return null;
  const cs = window.getComputedStyle(main);
  const w = parseFloat(cs.width) - 2 * FELT_INSET;
  const h = parseFloat(cs.height) - 2 * FELT_INSET;
  return w > 0 && h > 0 ? { w, h } : null;
}

/** A corner card lands this wide on the حاکم's avatar (it fades as it arrives) — the old fan's 0.6 of
 *  its 20 px. */
const KAF_LAND_W = 12;

/** A corner card's flight on a felt `felt` px: from its own centre (felt coordinates, laid out on the
 *  frame it flies in) to where it lands at the حاکم (`kafLanding`), shrinking to `KAF_LAND_W`. */
export function kafCornerAim(
  rel: number,
  felt: { w: number; h: number },
  from: { x: number; y: number },
  cardW: number,
): string {
  const [toX, toY] = kafLanding(rel, felt.w, felt.h);
  const x = Math.round(toX - from.x);
  const y = Math.round(toY - from.y);
  return `translate(${x}px, ${y}px) scale(${(KAF_LAND_W / cardW).toFixed(3)})`;
}

/**
 * THE کف's PICKUP on the felt (live frames only, `useKafMoments`): the four leave the felt's کف for
 * the حاکم, `KAF_STAGGER_MS` apart, `KAF_FLIGHT_MS` each, shrinking and fading as they arrive —
 * face-down to its seat on every other screen; face-UP toward YOUR edge when the حاکم is you (the
 * same four deal into your fan as they go, marked). They start where the auction left them (`corner`:
 * the table's latch of the last auction frame's `kafCornerPlacement`) and each AIMS as it starts, off
 * the frame it flies in: from its own laid-out centre to the حاکم's avatar (`feltNow`, `kafLanding`).
 * WAAPI through `Departing` (card-motion's easing): a player who asked for reduced motion sees the
 * four at rest for the moment's length, no flight.
 */
export function KafPickupFlight({
  rel,
  kaf,
  corner,
}: {
  rel: number;
  kaf: readonly string[];
  corner: KafCorner;
}): ReactNode {
  if (corner.form === "corner") {
    const w = corner.cardW;
    const aimCorner = (el: HTMLElement): string => {
      const felt = feltNow(el);
      const feltEl = el.closest<HTMLElement>("[data-felt]");
      if (felt === null || feltEl === null) return "translate(0px, 0px)";
      const f = feltEl.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      const from = { x: b.left + b.width / 2 - f.left, y: b.top + b.height / 2 - f.top };
      return kafCornerAim(rel, felt, from, w);
    };
    // card-98 item 9 (prototype): each card leaves from where the fan lays it — its own box, centred
    // on the card, so the aim (off that box's centre) is the card's; it keeps its turn as it flies
    const fan = kafCornerLayout(w, corner.step, WIDOW_SIZE);
    return (
      <div
        data-testid="shelem-kaf-flight"
        data-kaf-form="corner"
        aria-hidden="true"
        className="pointer-events-none absolute z-20"
        style={cornerBox(corner, WIDOW_SIZE)}
      >
        {fan.cards.map((card, i) => (
          <Departing
            key={i}
            to={aimCorner}
            delay={i * KAF_STAGGER_MS}
            duration={KAF_FLIGHT_MS}
            style={fanCardBox(card, w)}
          >
            {fanned(card, w, rel === 0 ? (kaf[i] ?? null) : null)}
          </Departing>
        ))}
      </div>
    );
  }
  const aim = (el: HTMLElement): string => {
    const felt = feltNow(el);
    const { x, y } = felt === null ? { x: 0, y: 0 } : kafPickupVector(rel, felt.w, felt.h);
    return `translate(${x}px, ${y}px) scale(0.6)`;
  };
  return (
    <div
      data-testid="shelem-kaf-flight"
      data-kaf-form="partner"
      aria-hidden="true"
      className="pointer-events-none absolute z-20"
      style={KAF_SPOT}
    >
      {Array.from({ length: WIDOW_SIZE }).map((_u, i) => (
        <Departing
          key={i}
          to={aim}
          delay={i * KAF_STAGGER_MS}
          duration={KAF_FLIGHT_MS}
          style={{ left: i * WIDOW_STEP, bottom: 0 }}
        >
          {kafFan(i, rel === 0 ? (kaf[i] ?? null) : null)}
        </Departing>
      ))}
    </div>
  );
}

/**
 * THE کف LAID DOWN (live frames only): four face-down cards leave the حاکم for its pile — swept
 * toward the حاکم's own edge as a won trick is collected (`collectSweep`), fading, on every screen.
 * Their faces never show: the discards stay hidden until the hand ends (Task 7 reveals them).
 */
export function KafDiscardSweep({ rel }: { rel: number }): ReactNode {
  const from: CSSProperties =
    rel === 0
      ? { left: `calc(50% - ${KAF_SPOT_W / 2}px)`, bottom: 10 }
      : rel === 1
        ? { right: 64, top: `calc(50% - ${(50 * WIDOW_SCALE) / 2}px)` }
        : rel === 3
          ? { left: 64, top: `calc(50% - ${(50 * WIDOW_SCALE) / 2}px)` }
          : { top: PARTNER_TOP_IN_FELT, right: KAF_SPOT.right };
  return (
    <div
      data-testid="shelem-kaf-sweep"
      aria-hidden="true"
      className="pointer-events-none absolute z-20"
      style={{ ...from, height: 50 * WIDOW_SCALE, width: KAF_SPOT_W }}
    >
      <Departing
        to={`${collectSweep(rel)} scale(0.6)`}
        delay={0}
        duration={KAF_DISCARD_SWEEP_MS}
        style={{ inset: 0 }}
      >
        {Array.from({ length: WIDOW_SIZE }).map((_u, i) => (
          <span key={i} className="absolute bottom-0" style={{ left: i * WIDOW_STEP }}>
            {kafFan(i, null)}
          </span>
        ))}
      </Departing>
    </div>
  );
}
